import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from './useQuery';
import { useMutation } from './useMutation';
import { useNotification } from '../contexts/NotificationContext';
import { getGradesByClass } from '../api/endpoints/grades';

// Importar service usando require para compatibilidade
const gradesService = require('../services/gradesService');

/**
 * Hook para gerenciar notas
 * 
 * @param {Object} filters - Filtros para buscar notas
 * @param {number} filters.TurmaID - ID da turma
 * @param {number} filters.DisciplinaID - ID da disciplina
 * @param {number} filters.period - Período (1-4)
 * 
 * @returns {Object} { grades, loading, error, refetch, updateGrade, updateAllGrades }
 */
export const useGrades = (filters) => {
  // const { showSuccess, showError } = useNotification(); // Não utilizado
  const [grades, setGrades] = useState([]);

  // Verificar se os filtros estão completos
  const isEnabled = !!(filters?.TurmaID && filters?.DisciplinaID && filters?.period);
  // Query para buscar notas
  const { data, loading, error, refetch } = useQuery(
    async (signal) => {
      // signal é opcional, pode ser ignorado se não for usado
      if (!filters?.TurmaID || !filters?.DisciplinaID || !filters?.period) {
        return { grades: [] };
      }

      const result = await getGradesByClass(filters);
      setGrades(result);
      return { grades: result };
    },
    [filters?.TurmaID, filters?.DisciplinaID, filters?.period],
    {
      enabled: isEnabled,
      showError: true,
    }
  );
  
  // Mutation para atualizar uma nota
  const updateGradeMutation = useMutation(
    async (gradeData) => {
      const result = await gradesService.createOrUpdateGrade(gradeData);
      
      // Atualizar estado local
      setGrades((prev) =>
        prev.map((g) =>
          g.AlunoID === gradeData.AlunoID &&
          g.DisciplinaID === gradeData.DisciplinaID &&
          g.period === gradeData.period
            ? { ...g, ...gradeData }
            : g
        )
      );
      
      return result;
    },
    {
      showSuccess: true,
      successMessage: 'Nota atualizada com sucesso!',
      showError: true,
    }
  );

  // Mutation para atualizar todas as notas
  const updateAllGradesMutation = useMutation(
    async (updateData) => {
      // Implementar lógica de atualização em massa
      // Por enquanto, atualizar uma por uma
      const promises = updateData.grades.map((grade) =>
        gradesService.createOrUpdateGrade(grade)
      );
      
      await Promise.all(promises);
      
      // Refetch para atualizar dados
      await refetch();
      
      return { success: true };
    },
    {
      showSuccess: true,
      successMessage: 'Notas atualizadas com sucesso!',
      showError: true,
    }
  );

  // Função helper para calcular notas automaticamente
  const calculateGrades = useCallback((at1, at2, at3, at4, at5, at6) => {
    const ac1 = gradesService.calculateAC1(at1, at2);
    const ac2 = gradesService.calculateAC2(at3, at4, at5, at6);
    const mb = gradesService.calculateMB(ac1, ac2);

    return { ac1, ac2, mb };
  }, []);

  // Constante para array vazio - evitar criar nova referência
  const EMPTY_ARRAY = useMemo(() => [], []);

  // Memoizar o array de grades para evitar criar nova referência a cada render
  const memoizedGrades = useMemo(() => {
    if (grades.length > 0) {
      return grades;
    }
    if (data?.grades && Array.isArray(data.grades) && data.grades.length > 0) {
      return data.grades;
    }
    return EMPTY_ARRAY; // Retornar sempre o mesmo array vazio memoizado
  }, [grades, data?.grades, EMPTY_ARRAY]);

  return {
    grades: memoizedGrades,
    loading: loading || updateGradeMutation.loading || updateAllGradesMutation.loading,
    error: error || updateGradeMutation.error || updateAllGradesMutation.error,
    refetch,
    updateGrade: updateGradeMutation.mutate,
    updateGradeAsync: updateGradeMutation.mutateAsync,
    updateAllGrades: updateAllGradesMutation.mutate,
    updateAllGradesAsync: updateAllGradesMutation.mutateAsync,
    calculateGrades,
  };
};

